import { useState, useEffect } from 'react';
import { 
  Container, 
  Grid, 
  Card,
  CardContent, 
  Typography, 
  Box,
  CircularProgress,
  Button,
  Chip,
  Pagination,
  IconButton,
  Rating
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import FilterDialog from '../FilterDialog/FilterDialog';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES } from '../../utils/roles';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import EditIcon from '@mui/icons-material/Edit';
import { entrepriseService } from '../../services/entrepriseService';
import { authService } from '../../services/authService';

function Entreprises() {
  const [entreprises, setEntreprises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [activeFilters, setActiveFilters] = useState({
    nom: '',
    secteur: [],
    localisation: [],
    rating: []
  });

  const { userRole, currentUser } = useAuth();
  const [entreprisesEvaluations, setEntreprisesEvaluations] = useState({});
  const [userRatings, setUserRatings] = useState({});

  // Ajouter l'état pour le scroll
  const [scrollY, setScrollY] = useState(0);

  // Ajouter l'effet pour suivre le scroll
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchEntreprises = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/entreprises?page=${page}`, {
          headers: {
            'Accept': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Erreur lors du chargement des entreprises');
        }

        const responseData = await response.json();
        
        // Mise à jour avec les données paginées du backend
        setEntreprises(responseData.data);
        setTotalPages(responseData.last_page);
        
        // Extraire les options uniques pour les filtres
        const secteurs = [...new Set(responseData.data.map(e => e.secteur))].filter(Boolean);
        const localisations = [...new Set(responseData.data.map(e => e.localisation))].filter(Boolean);
        
        setAvailableFilters(prev => ({
          ...prev,
          secteur: { ...prev.secteur, options: secteurs },
          localisation: { ...prev.localisation, options: localisations }
        }));

      } catch (err) {
        console.error('Erreur fetchEntreprises:', err);
        setError("Impossible de charger les entreprises");
      } finally {
        setLoading(false);
      }
    };

    fetchEntreprises();
  }, [page]); // Recharger quand la page change

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pageParam = parseInt(params.get('page')) || 1;
    setPage(pageParam);
  }, [location.search]);

  // Récupérer toutes les moyennes (accessible à tous)
  useEffect(() => {
    const fetchMoyennes = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/entreprises/evaluations/moyennes`);
        if (response.ok) {
          const data = await response.json();
          const moyennes = {};
          data.entreprises.forEach(e => {
            moyennes[e.id] = {
              moyenne: e.moyenne,
              nom: e.nom
            };
          });
          setEntreprisesEvaluations(moyennes);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des moyennes:', error);
      }
    };

    fetchMoyennes();
  }, []);

  // Récupérer les évaluations de l'utilisateur si admin/pilote
  useEffect(() => {
    const fetchUserRatings = async () => {
      if (!currentUser) return;

      try {
        let endpoint;
        if (userRole === ROLES.ADMIN) {
          endpoint = '/admin/entreprises/evaluations/me';
        } else if (userRole === ROLES.PILOTE) {
          endpoint = '/pilote/entreprises/evaluations/me';
        } else {
          return;
        }

        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}${endpoint}`, {
          headers: {
            'Authorization': `Bearer ${authService.getToken()}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          const ratings = {};
          data.evaluations.forEach(evaluation => {
            ratings[evaluation.entreprise.id] = {
              id: evaluation.id,
              note: evaluation.note
            };
          });
          setUserRatings(ratings);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des évaluations:', error);
      }
    };

    fetchUserRatings();
  }, [currentUser, userRole]);

  // Dans le useEffect pour charger les statistiques
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/statistiques`, {
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        });

        if (response.ok) {
          const data = await response.json();
        }
      } catch (error) {
        console.error('Erreur lors du chargement des statistiques:', error);
      }
    };

    fetchStats();
  }, []);

  const getAverageRating = (entrepriseId) => {
    const evaluation = entreprisesEvaluations[entrepriseId];
    return evaluation ? evaluation.moyenne : 0;
  };

  const getRatingValue = (ratingStr) => {
    return parseInt(ratingStr.match(/\d/)[0]);
  };

  const [availableFilters, setAvailableFilters] = useState({
    nom: { 
      label: 'Nom de l\'entreprise', 
      type: 'text' 
    },
    secteur: { 
      label: 'Secteur', 
      type: 'select',
      options: [],
      multiple: true
    },
    localisation: { 
      label: 'Localisation', 
      type: 'select',
      options: [],
      multiple: true
    },
    rating: {
      label: 'Note minimale',
      type: 'select',
      options: [
        '⭐️ (1 étoile ou plus)',
        '⭐️⭐️ (2 étoiles ou plus)',
        '⭐️⭐️⭐️ (3 étoiles ou plus)',
        '⭐️⭐️⭐️⭐️ (4 étoiles ou plus)',
        '⭐️⭐️⭐️⭐️⭐️ (5 étoiles)'
      ],
      multiple: false
    }
  });

  const filteredEntreprises = entreprises.filter(entreprise => {
    return Object.entries(activeFilters).every(([key, value]) => {
      if (!value || (Array.isArray(value) && value.length === 0)) return true;
      
      if (key === 'nom') {
        return entreprise[key].toLowerCase().includes(value.toLowerCase());
      }
      
      if (key === 'rating') {
        const averageRating = getAverageRating(entreprise.id_entreprises);
        if (value.length === 0) return true;
        const minimumRating = getRatingValue(value[0]);
        return averageRating >= minimumRating;
      }
      
      if (Array.isArray(value)) {
        return value.includes(entreprise[key]);
      }
      
      return entreprise[key] === value;
    });
  });

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    navigate(`/entreprises?page=${newPage}`);
    window.scrollTo({
      top: document.getElementById('entreprises-list').offsetTop - 20,
      behavior: 'smooth'
    });
  };

  const handleApplyFilters = (newFilters) => {
    setActiveFilters(newFilters);
    setPage(1); // Retour à la première page lors de l'application des filtres
  };

  const handleCardClick = (e, entrepriseId) => {
    // Empêcher la navigation si on clique sur les étoiles
    if (e.target.closest('.MuiRating-root')) {
      e.stopPropagation();
      return;
    }

    if (e.target.closest('.edit-button')) {
      e.stopPropagation();
      navigate(`/entreprises/${entrepriseId}/edit`);
      return;
    }
    
    navigate(`/entreprises/${entrepriseId}`);
  };

  const handleRating = async (entrepriseId, newValue) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/entreprises/${entrepriseId}/evaluer`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authService.getToken()}`
          },
          body: JSON.stringify({ note: newValue })
        }
      );

      if (!response.ok) {
        throw new Error('Erreur lors de l\'évaluation');
      }

      const data = await response.json();
      
      // Mettre à jour le rating de l'utilisateur
      setUserRatings(prev => ({
        ...prev,
        [entrepriseId]: { id: data.evaluation.id, note: newValue }
      }));

      // Mettre à jour la moyenne
      const moyenneResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/entreprises/${entrepriseId}/evaluations/moyenne`
      );
      
      if (moyenneResponse.ok) {
        const moyenneData = await moyenneResponse.json();
        setEntreprisesEvaluations(prev => ({
          ...prev,
          [entrepriseId]: {
            ...prev[entrepriseId],
            moyenne: moyenneData.moyenne
          }
        }));
      }
    } catch (error) {
      console.error('Erreur lors de l\'évaluation:', error);
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress color="primary" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Typography color="error" align="center">
          {error}
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography 
        variant="h3" 
        component="h1" 
        align="center" 
        gutterBottom
        sx={{
          opacity: 0,
          animation: 'fadeIn 0.8s ease-out forwards',
          '@keyframes fadeIn': {
            '0%': { opacity: 0 },
            '100%': { opacity: 1 }
          }
        }}
      >
        Nos Entreprises Partenaires
      </Typography>

      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        mb: 4, 
        gap: 1, 
        flexWrap: 'wrap',
        opacity: 0,
        animation: 'fadeIn 0.8s ease-out 0.3s forwards',
        '@keyframes fadeIn': {
          '0%': { opacity: 0 },
          '100%': { opacity: 1 }
        }
      }}>
        {(userRole === ROLES.ADMIN || userRole === ROLES.PILOTE) && (
          <Button
            variant="contained"
            startIcon={<AddBusinessIcon />}
            onClick={() => navigate('/entreprises/new')}
            color="primary"
            sx={{ mb: 2, mr: 2 }}
          >
            Ajouter une entreprise
          </Button>
        )}
        <Button
          variant="outlined"
          startIcon={<FilterListIcon />}
          onClick={() => setFilterDialogOpen(true)}
          sx={{ mb: 2 }}
        >
          Filtrer
        </Button>
        {Object.entries(activeFilters).map(([key, value]) => (
          (value && (Array.isArray(value) ? value.length > 0 : value !== '')) && (
            <Chip
              key={key}
              label={`${availableFilters[key].label}: ${Array.isArray(value) ? value.join(', ') : value}`}
              onDelete={() => {
                setActiveFilters(prev => ({
                  ...prev,
                  [key]: Array.isArray(value) ? [] : ''
                }));
              }}
              sx={{ mb: 2 }}
            />
          )
        ))}
      </Box>

      <FilterDialog
        open={filterDialogOpen}
        onClose={() => setFilterDialogOpen(false)}
        onApply={handleApplyFilters}
        filters={availableFilters}
        activeFilters={activeFilters}
        title="Filtrer les entreprises"
      />

      <Box id="entreprises-list">
        <Grid container spacing={4} justifyContent="center">
          {entreprises.map((entreprise, index) => (
            <Grid 
              item 
              xs={12} 
              sm={6} 
              md={4} 
              key={entreprise.id_entreprises}
              sx={{ 
                maxWidth: '400px',
                ...(index < 3 ? {
                  opacity: 0,
                  animation: `fadeIn 0.4s ease-out ${0.2 + index * 0.1}s forwards`,
                  '@keyframes fadeIn': {
                    '0%': { opacity: 0, transform: 'translateY(20px)' },
                    '100%': { opacity: 1, transform: 'translateY(0)' }
                  }
                } : {
                  opacity: window.scrollY > (100 + (index - 3) * 30) ? 1 : 0,
                  transform: `translateY(${window.scrollY > (100 + (index - 3) * 30) ? 0 : 20}px)`,
                  transition: 'all 0.3s ease-out',
                })
              }}
            >
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'scale(1.02)',
                    cursor: 'pointer'
                  },
                  position: 'relative'
                }}
                onClick={(e) => handleCardClick(e, entreprise.id_entreprises)}
              >
                {(userRole === ROLES.ADMIN || userRole === ROLES.PILOTE) && (
                  <IconButton
                    className="edit-button"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      bgcolor: 'background.paper',
                      zIndex: 1,
                      '&:hover': {
                        bgcolor: 'primary.main'
                      }
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                )}
                <Box 
                  sx={{ 
                    height: 200,
                    backgroundColor: 'background.default',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2
                  }}
                >
                  <img
                    src={entreprise.logo || 'https://via.placeholder.com/150'}
                    alt={entreprise.nom}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain'
                    }}
                  />
                </Box>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography gutterBottom variant="h5" component="h2">
                    {entreprise.nom}
                  </Typography>
                  {(userRole === ROLES.ADMIN || userRole === ROLES.PILOTE) && (
                    <Rating
                      name={`rating-${entreprise.id_entreprises}`}
                      value={userRatings[entreprise.id_entreprises]?.note || 0}
                      onChange={(event, newValue) => handleRating(entreprise.id_entreprises, newValue)}
                    />
                  )}
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {entreprise.description}
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Secteur:</strong> {entreprise.secteur}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Localisation:</strong> {entreprise.localisation}
                    </Typography>
                    {entreprise.siteWeb && (
                      <Typography variant="body2" color="text.secondary">
                        <strong>Site Web:</strong> {entreprise.siteWeb}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="body2">
                    Note moyenne : {(entreprisesEvaluations[entreprise.id_entreprises]?.moyenne || 0).toFixed(1)} / 5
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Pagination avec animation basée sur le temps */}
        {totalPages > 1 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mt: 4,
              mb: 2,
              opacity: 0,
              animation: 'fadeIn 0.8s ease-out 1.5s forwards',
              '@keyframes fadeIn': {
                '0%': { opacity: 0, transform: 'translateY(20px)' },
                '100%': { opacity: 1, transform: 'translateY(0)' }
              }
            }}
          >
            <Pagination 
              count={totalPages}
              page={page}
              onChange={handleChangePage}
              color="primary"
              size="large"
            />
          </Box>
        )}

        {/* Message si aucune entreprise */}
        {filteredEntreprises.length === 0 && (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="h6" color="text.secondary">
              Aucune entreprise ne correspond à vos critères
            </Typography>
          </Box>
        )}
      </Box>
    </Container>
  );
}

export default Entreprises; 